import React, { useState, useRef, useEffect } from 'react';
import { FaTrash, FaPaperclip, FaTimes, FaRegSmile } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useDarkMode } from '../contexts/DarkModeContext';

// Security: Rate limiting constants
const RATE_LIMIT_WINDOW = 60000; // 1 minute
const MAX_MESSAGES_PER_MINUTE = 50; // 50 messages per minute

// Security: Rate limiting state
const rateLimitMap = new Map();

// Security: Rate limiting function
const checkRateLimit = (userId) => {
  const now = Date.now();
  const userRateLimit = rateLimitMap.get(userId) || { count: 0, resetTime: now + RATE_LIMIT_WINDOW };
  
  if (now > userRateLimit.resetTime) {
    userRateLimit.count = 1;
    userRateLimit.resetTime = now + RATE_LIMIT_WINDOW;
  } else {
    userRateLimit.count++;
  }
  
  rateLimitMap.set(userId, userRateLimit);
  
  if (userRateLimit.count > MAX_MESSAGES_PER_MINUTE) {
    return { allowed: false, remainingTime: userRateLimit.resetTime - now };
  }
  
  return { allowed: true, remainingMessages: MAX_MESSAGES_PER_MINUTE - userRateLimit.count };
};

// Security: Input validation function
const validateInput = (input) => {
  if (!input || typeof input !== 'string') return { isValid: false, error: 'Invalid input' };
  if (input.trim().length === 0) return { isValid: false, error: 'Message cannot be empty' };
  if (input.length > 1000) return { isValid: false, error: 'Message too long (max 1000 characters)' };
  return { isValid: true };
};

// Security: Emoji validation
const validateEmoji = (emoji) => {
  return emoji && typeof emoji === 'string' && emoji.length <= 4;
};

const InputArea = ({ 
  user, 
  conversation, 
  onSendMessage, 
  onFileUpload, 
  sending, 
  uploading, 
  uploadProgress, 
  uploadingFile,
  isOnline,
  offlineQueue,
  replyToMessage,
  onCancelReply
}) => {
  const { isDarkMode } = useDarkMode();

  const [input, setInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Focus the input after sending is done (no animation delay)
  useEffect(() => {
    if (!sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sending]);

  // Emoji picker click outside handler
  useEffect(() => {
    if (!showEmojiPicker) return;
    function handleClickOutside(event) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // ⚡️ INSTANT MESSAGING: Create message immediately on button click
  const handleSendButtonClick = (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    // Security: Rate limiting check
    const rateLimit = checkRateLimit(user?.uid || user?.email);
    if (!rateLimit.allowed) {
      const remainingSeconds = Math.ceil(rateLimit.remainingTime / 1000);
      if (typeof toast !== 'undefined') {
        toast.error(`Rate limit exceeded. Please wait ${remainingSeconds} seconds.`);
      } else {
        alert(`Rate limit exceeded. Please wait ${remainingSeconds} seconds.`);
      }
      return;
    }

    // Security: Input validation
    const validation = validateInput(input);
    if (!validation.isValid) {
      if (typeof toast !== 'undefined') {
        toast.error(validation.error);
      } else {
        alert(validation.error);
      }
      return;
    }

    // Send message
    onSendMessage(input);
    setInput('');
    setShowEmojiPicker(false);
  };

  const handleSend = (e) => {
    e.preventDefault();
    handleSendButtonClick(e);
  };

  const handleEmojiClick = (emoji) => {
    if (!validateEmoji(emoji)) return;
    setInput(input + emoji);
    setShowEmojiPicker(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await onFileUpload(file);
  };

  const canSendMessage = input.trim().length > 0 && !sending && !uploading;

  return (
    <div className="flex flex-col mx-4 mb-2">
      {/* Enhanced Offline Indicator */}
      {!isOnline && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mx-4 mb-3 w-full">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>You're offline</strong> - Messages will be sent when you're back online
                {offlineQueue && offlineQueue.length > 0 && (
                  <span className="ml-1">({offlineQueue.length} queued)</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <form
        className={`relative w-full flex flex-col rounded-2xl sm:rounded-3xl mx-2 sm:mx-0 ${
          isDarkMode ? 'bg-[#232323]' : 'bg-white border border-gray-200'
        }`}
        onSubmit={handleSend}
        autoComplete="off"
      >
        {/* 🎨 REPLY CONTEXT: Modern reply information inside input area */}
        {replyToMessage && (
          <div className={`p-4 backdrop-blur-sm rounded-t-2xl sm:rounded-t-3xl border-l-4 border-green-400 shadow-lg ${
            isDarkMode 
              ? 'bg-gradient-to-r from-gray-800/90 to-gray-700/90 border border-gray-600/30' 
              : 'bg-gradient-to-r from-green-50 to-green-100 border border-green-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <div className="text-xs text-green-300 font-semibold tracking-wide uppercase">
                    Replying to {replyToMessage.name || replyToMessage.from?.split('@')[0] || 'User'}
                  </div>
                </div>
                <div className={`text-xs truncate leading-relaxed font-medium ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  {replyToMessage.text && replyToMessage.text.length > 50 
                    ? replyToMessage.text.substring(0, 50) + '...' 
                    : replyToMessage.text || 'Message'}
                </div>
              </div>
              <button
                type="button"
                onClick={onCancelReply}
                className={`ml-3 p-2 rounded-full transition-all duration-200 ${
                  isDarkMode 
                    ? 'text-gray-400 hover:text-white hover:bg-gray-600/50' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title="Cancel reply"
              >
                <FaTimes size={12} />
              </button>
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-1 sm:gap-2 py-3 sm:py-4 pr-3 sm:pr-4 pl-3 sm:pl-4">
          <button
            type="button"
            onClick={() => setShowEmojiPicker(v => !v)}
            title="Emoji"
            className={`flex items-center justify-center rounded-full w-8 h-8 sm:w-10 sm:h-10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${
              isDarkMode 
                ? 'text-gray-400 hover:text-yellow-400' 
                : 'text-gray-500 hover:text-yellow-500'
            }`}
            tabIndex={-1}
          >
            <FaRegSmile size={18} className="sm:w-5 sm:h-5" />
          </button>
          
          <input
            type="file"
            accept="image/*,video/*"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          
          <button
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            title="Attach image or video"
            className={`flex items-center justify-center rounded-full w-8 h-8 sm:w-10 sm:h-10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-400 relative ${
              isDarkMode 
                ? 'text-gray-400 hover:text-red-400' 
                : 'text-gray-500 hover:text-red-500'
            }`}
            tabIndex={-1}
            disabled={uploading}
          >
            {uploading && uploadingFile ? (
              <div className="relative w-full h-full flex items-center justify-center">
                {/* WhatsApp-style circular progress */}
                <svg width="100%" height="100%" viewBox="0 0 40 40" className="absolute inset-0">
                  {/* Background circle */}
                  <circle 
                    cx="20" 
                    cy="20" 
                    r="16" 
                    stroke="#374151" 
                    strokeWidth="3" 
                    fill="none" 
                  />
                  {/* Progress circle */}
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    stroke="#10B981"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 16}
                    strokeDashoffset={2 * Math.PI * 16 * (1 - uploadProgress / 100)}
                    style={{ transition: 'stroke-dashoffset 0.2s linear' }}
                  />
                </svg>
                {/* File type icon in center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {uploadingFile.type.startsWith('image/') ? (
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                  ) : uploadingFile.type.startsWith('video/') ? (
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a2 2 0 00-2 2v1a2 2 0 002 2h2a2 2 0 002-2v-1h2a1 1 0 011 1v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <FaPaperclip size={12} className="text-green-500" />
                  )}
                </div>
              </div>
            ) : (
              <FaPaperclip size={16} className="sm:w-5 sm:h-5" />
            )}
          </button>
          
          <textarea
            placeholder="Write a message... (or drag files here)"
            className={`flex-1 bg-transparent px-2 py-2 focus:outline-none resize-none overflow-y-auto min-h-[40px] sm:min-h-[44px] max-h-[120px] sm:max-h-[200px] w-full text-sm sm:text-base ${
              isDarkMode 
                ? 'text-white placeholder-gray-400' 
                : 'text-gray-900 placeholder-gray-500'
            }`}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendButtonClick(e);
              }
            }}
            disabled={sending}
            ref={inputRef}
            rows={1}
          />
          
          <button
            type="button"
            onClick={handleSendButtonClick}
            className={`flex items-center justify-center rounded-full w-8 h-8 sm:w-10 sm:h-10 transition-all duration-200 ${
              !canSendMessage 
                ? (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-300 text-gray-500') + ' cursor-not-allowed' 
                : sending 
                  ? 'bg-green-500 text-white' 
                  : 'bg-red-500 hover:bg-red-600 text-white cursor-pointer'
            }`}
            disabled={!canSendMessage}
          >
            {uploading ? (
              // ⚡️ UPLOAD ANIMATION: Circular progress with upload icon
              <div className="relative w-full h-full flex items-center justify-center">
                <svg width="100%" height="100%" viewBox="0 0 40 40" className="absolute inset-0">
                  <circle 
                    cx="20" 
                    cy="20" 
                    r="16" 
                    stroke="#374151" 
                    strokeWidth="3" 
                    fill="none" 
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="16"
                    stroke="#EF4444"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 16}
                    strokeDashoffset={2 * Math.PI * 16 * (1 - uploadProgress / 100)}
                    style={{ transition: 'stroke-dashoffset 0.2s linear' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            ) : sending ? (
              // ⚡️ INSTANT SENDING: Static send icon (no animation delay)
              <div className="relative w-full h-full flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-7.5-15-7.5v6l10 1.5-10 1.5v6z" />
                </svg>
              </div>
            ) : (
              // ⚡️ NORMAL STATE: Send arrow icon (no animation delay)
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-7.5-15-7.5v6l10 1.5-10 1.5v6z" />
              </svg>
            )}
          </button>
        </div>
        
        {showEmojiPicker && (
          <div ref={emojiPickerRef} className="absolute bottom-full left-0 mb-2 bg-white border rounded-lg shadow-lg p-2 flex flex-wrap gap-1 w-48 sm:w-56 z-20">
            {['😀','😁','😂','🤣','😊','😍','😎','😢','😡','👍','🙏','🎉','❤️','🔥','😅','😉','😇','🥳','🤔'].map(e => (
              <button
                key={e}
                type="button"
                className="text-xl sm:text-2xl p-1 hover:bg-gray-100 rounded"
                onClick={() => handleEmojiClick(e)}
                disabled={sending}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};

export default InputArea; 